(ns portal.adserver-configuration-ocp.db
  (:require [clojure.set :refer [rename-keys]]
            [clojure.spec.alpha :as spec]
            [common.flags :refer [use-flag]]
            [common.form.core :as form]
            [common.queries :refer [use-query]]
            [common.resource.query.adserver :as adserver.q]
            [common.resource.query.adserver-account :as adserver-account.q]
            [common.resource.query.adserver-account-configuration :as adserver-config.q]
            [common.util :refer [not-blank?]]
            [re-frame.core :as rf]
            [uix.core :refer [defhook]]
            [uix.re-frame :refer [use-subscribe]]))

(def entity-name "Adserver")

(spec/def ::adserveraccountId int?)

(spec/def ::apiAdvertiserId
  (spec/and string?
            not-blank?))

(spec/def ::value
  (spec/keys :req-un [::adserveraccountId ::apiAdvertiserId]))

(def initial-value
  {:adserveraccountId nil
   :isDefault false})

(def validate-form
  (rf/enrich
   (form/get-validate-form-fn :form.adserver-configuration ::value)))

(def ui-messages
  {:create {:success "Successfully created configuration"
            :failure "Failed to create configuration"}
   :edit {:success "Successfully saved configuration"
          :failure "Failed to save configuration"}
   :delete {:success "Successfully deleted configuration"
            :failure "Failed to delete configuration"}})

(def forward-events [{:events #{:resource.adserver-account-configuration/post-success
                                :resource.adserver-account-configuration/patch-success}
                      :dispatch-to [:form.adserver-configuration/submit-success]}
                     {:events #{:resource.adserver-account-configuration/post-failure
                                :resource.adserver-account-configuration/patch-failure}
                      :dispatch-to [:form.adserver-configuration/submit-failure]}
                     {:events #{:resource.adserver-account-configuration/delete-success}
                      :dispatch-to [:form.adserver-configuration/delete-success]}
                     {:events #{:resource.adserver-account-configuration/delete-failure}
                      :dispatch-to [:form.adserver-configuration/delete-failure]}])

(form/register-form {:namespace :form.adserver-configuration
                     :initial-value initial-value
                     :validate-form validate-form
                     :forward-events forward-events
                     :submit-event :form.adserver-configuration/submit-form})

(def use-bind-control (partial form/use-bind-control :form.adserver-configuration))
(def use-error-message (partial form/use-error-message :form.adserver-configuration))

(defmulti submit-event :mode)

(defmethod submit-event :edit [{:keys [payload]}]
  (let [configuration-id (:id payload)
        payload (select-keys payload [:apiAdvertiserId :isDefault :hasAutoTagging :lowercaseClicktag2])]
    [:resource.adserver-account-configuration/patch configuration-id payload]))

(defmethod submit-event :create [{:keys [payload]}]
  (let [payload (select-keys payload [:adserveraccountId :apiAdvertiserId :isDefault :hasAutoTagging :lowercaseClicktag2])]
    [:resource.adserver-account-configuration/post payload]))

(rf/reg-event-fx :form.adserver-configuration/init-form
  (fn [{:keys [db]} [_ adserver-configuration]]
    (let [adserver-configuration (into {} (filter (comp some? val) adserver-configuration))
          current-adserver (:adserveraccount adserver-configuration)]
      {:db (cond-> db
             (seq adserver-configuration) (update-in [:form.adserver-configuration] assoc
                                                     :value adserver-configuration
                                                     :meta {:current-adserver current-adserver}
                                                     :mode :edit)
             (empty? adserver-configuration) (assoc-in [:form.adserver-configuration :mode] :create))
       :dispatch-n [[:form.adserver-configuration/init]]})))

(rf/reg-event-fx :form.adserver-configuration/submit-form
  [(rf/inject-cofx :sub :form.adserver-configuration/mode)]
  (fn [{:form.adserver-configuration/keys [mode]} [_ payload]]
    {:dispatch-n [[:loader/open]
                  (submit-event {:mode mode
                                 :payload payload})]}))

(rf/reg-event-fx :form.adserver-configuration/submit-success
  [(rf/inject-cofx :sub :form.adserver-configuration/mode)
   (rf/inject-cofx :sub :url-context)]
  (fn [{:form.adserver-configuration/keys [mode]
        :keys [url-context]} _]
    {:fx [[:dispatch [:ocp/close]]
          [:dispatch [:loader/close]]
          [:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :success
                       :message (get-in ui-messages [mode :success])}]]
          [:invalidate-queries {:queryKey ["adserver-account-configurations" (:advertiser-id url-context)]}]]}))

(rf/reg-event-fx :form.adserver-configuration/submit-failure
  [(rf/inject-cofx :sub :form.adserver-configuration/mode)]
  (fn [{:form.adserver-configuration/keys [mode]} [event response]]
    {:dispatch-n [[:loader/close]
                  [:response-handler/handle-response
                   {:entity-name entity-name
                    :type :error
                    :message (get-in ui-messages [mode :failure])
                    :event event
                    :response response}]]}))

(rf/reg-event-fx :form.adserver-configuration/delete
  (fn [{:keys [db]} _]
    (let [configuration-id (get-in db [:form.adserver-configuration :value :id])]
      {:dispatch-n [[:loader/open]
                    [:resource.adserver-account-configuration/delete configuration-id]]})))

(rf/reg-event-fx :form.adserver-configuration/delete-success
  [(rf/inject-cofx :sub :url-context)]
  (fn [{:keys [url-context]} _]
    {:fx [[:dispatch [:ocp/close]]
          [:dispatch [:loader/close]]
          [:dispatch [:response-handler/handle-response
                      {:entity-name entity-name
                       :type :success
                       :message (get-in ui-messages [:delete :success])}]]
          [:invalidate-queries {:queryKey ["adserver-account-configurations" (:advertiser-id url-context)]}]]}))

(rf/reg-event-fx :form.adserver-configuration/delete-failure
  (fn [_ [event response]]
    {:dispatch-n [[:loader/close]
                  [:response-handler/handle-response
                   {:entity-name entity-name
                    :type :error
                    :message (get-in ui-messages [:delete :failure])
                    :event event
                    :response response}]]}))

(rf/reg-event-fx :form.adserver-configuration/on-change-adserver
  [validate-form]
  (fn [{:keys [db]} [_ current-adserver]]
    (let [api-enabled? (pos? (:apiMethod current-adserver))]
      {:db (cond-> db
             true (assoc-in [:form.adserver-configuration :meta :current-adserver] current-adserver)
             api-enabled? (assoc-in [:form.adserver-configuration :value :apiAdvertiserId] "")
             (not api-enabled?) (update-in [:form.adserver-configuration :value] dissoc :apiAdvertiserId))})))

(defhook use-adserver-account-options []
  (let [{:keys [agency-id advertiser-id]} (use-subscribe [:url-context])
        {{adserver-accounts :_items} :data} (use-query (adserver-account.q/adserver-accounts-query {:agency-id agency-id}))
        {configurations :data} (use-query (adserver-config.q/adserver-account-configurations-query
                                           {:advertiser-id advertiser-id}))

        xandr-enabled? (use-flag :xandr-adserver-support)
        used-adserver-account-ids (set (map #(get-in % [:adserveraccount :id])
                                            configurations))]
    (cond->> adserver-accounts
      true (filter #((complement used-adserver-account-ids) (:id %)))
      (not xandr-enabled?) (remove (fn [adserver-account]
                                     (adserver.q/xandr-adserver? (get-in adserver-account [:adserver :id]))))
      true (map #(rename-keys % {:name :label
                                 :id :value})))))

(rf/reg-sub :form.adserver-configuration/current-adserver
  (fn [db _]
    (get-in db [:form.adserver-configuration :meta :current-adserver])))

(rf/reg-sub :form.adserver-configuration/mode
  (fn [db _]
    (get-in db [:form.adserver-configuration :mode])))
